import { type FC, useState, useCallback, useEffect } from 'react';
import { useFragment, graphql } from 'react-relay';
import { useSelector } from 'react-redux';
import { useWeb3Connection } from 'dibs-crypto-utils/exports/useWeb3Connection';
import { useIntl, FormattedMessage, defineMessages } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link';
import { ethereumPaymentSectionUrl } from 'dibs-crypto-utils/exports/constants';
import Checkmark from 'dibs-icons/exports/legacy/Checkmark';
import Copy from 'dibs-icons/exports/legacy/Copy';
import Close from 'dibs-icons/exports/legacy/Close';
import Twitter from 'dibs-icons/exports/legacy/Twitter';
import DiscordLogo from 'dibs-icons/exports/legacy/DiscordLogo';
import ExclamationCircle from 'dibs-icons/exports/legacy/ExclamationCircle';
import { shortAddress, shortName } from 'dibs-crypto-utils/exports/formattingUtils';
import { CreateNftButton } from './CreateNftButton';
import { type ConnectedWalletMenuContent_user$key } from './__generated__/ConnectedWalletMenuContent_user.graphql';
import styles from './styles/ConnectedWalletMenuContent.scss';

const messages = defineMessages({
    accountMismatch: {
        id: 'connectMetamask.tooltip.accountMismatch',
        defaultMessage: 'Account Mismatch',
    },
    unsupportedNetwork: {
        id: 'connectMetamask.tooltip.unsupportedNetwork',
        defaultMessage: 'Unsupported Network',
    },
    errorTooltipBody: {
        id: 'connectMetamask.tooltip.errorBody',
        defaultMessage: 'Please update your wallet in <a>wallet settings</a>.',
    },
    switchMetaMaskEthereumChain: {
        id: 'connectMetamask.tooltip.switchMetaMaskEthereumChain',
        defaultMessage: 'Please switch to {chainName}.',
    },
});

type Props = {
    user: ConnectedWalletMenuContent_user$key;
    onCloseButtonClick?: () => void;
    showCreateButton?: boolean;
    showEditorial?: boolean;
};

type ReduxStoreType = {
    header: {
        logoutUser: (redirectOverride: string) => void;
    };
};

export const ConnectedWalletMenuContent: FC<Props> = ({
    user: userRef,
    onCloseButtonClick,
    showCreateButton = false,
    showEditorial = false,
}) => {
    const logoutUser = useSelector<ReduxStoreType, (redirectOverride: string) => void>(
        ({ header }) => header.logoutUser
    );

    const user = useFragment(
        graphql`
            fragment ConnectedWalletMenuContent_user on User {
                ethereumWalletAddress {
                    address
                    ensDomain
                    publicProfile {
                        __typename
                    }
                }
            }
        `,
        userRef
    );
    const { formatMessage } = useIntl();
    const {
        account: connectedAddress,
        unsupportedChain,
        isMetaMaskInstalled,
        requiredChain,
        switchMetaMaskEthereumChain,
    } = useWeb3Connection();
    const { ethereumWalletAddress } = user || {};
    const { address: persistedAddress, publicProfile, ensDomain } = ethereumWalletAddress || {};
    const mismatchedAccounts =
        connectedAddress && connectedAddress.toLowerCase() !== persistedAddress?.toLowerCase();
    const hasError = mismatchedAccounts || unsupportedChain;

    const [hasClipboardApi, setHasClipboardApi] = useState(false);
    const [isCopied, setIsCopied] = useState(false);
    const copyToClipboard = useCallback(() => {
        if (hasClipboardApi) {
            try {
                navigator.clipboard.writeText(persistedAddress || '').then(
                    () => {
                        setIsCopied(true);
                    },
                    () => {
                        throw new Error();
                    }
                );
                setTimeout(() => {
                    setIsCopied(false);
                }, 2500);
            } catch (e) {
                // silently fail
            }
        }
    }, [hasClipboardApi, persistedAddress]);

    useEffect(() => {
        setHasClipboardApi(typeof navigator !== 'undefined' && !!navigator?.clipboard?.writeText);
    }, []);

    return (
        <>
            <div className={styles.accountMenuHeader}>
                <span className={styles.accountMenuHeaderAddress}>
                    {shortName(ensDomain || '') || shortAddress(persistedAddress || '')}
                </span>
                <Link
                    onClick={e => {
                        e.preventDefault();
                        copyToClipboard();
                    }}
                    className={styles.iconWrapper}
                >
                    {isCopied ? (
                        <Checkmark className={`${styles.copyIcon} ${styles.success}`} />
                    ) : (
                        <Copy className={styles.copyIcon} />
                    )}
                </Link>
                {onCloseButtonClick && (
                    <Link onClick={onCloseButtonClick} className={styles.closeButtonWrapper}>
                        <Close className={styles.closeButton} />
                    </Link>
                )}
            </div>
            {hasError && (
                <div className={styles.errorWrapper}>
                    <div className={styles.errorTitle}>
                        <ExclamationCircle className={styles.exclamationIconSmall} />
                        <p className={styles.tooltipError}>
                            {formatMessage(
                                unsupportedChain
                                    ? messages.unsupportedNetwork
                                    : messages.accountMismatch
                            )}
                        </p>
                    </div>
                    <p className={styles.errorBody}>
                        {isMetaMaskInstalled && unsupportedChain ? (
                            <Link
                                onClick={switchMetaMaskEthereumChain}
                                key="switchMetaMaskEthereumChain"
                            >
                                {formatMessage(messages.switchMetaMaskEthereumChain, {
                                    chainName: requiredChain.name,
                                })}
                            </Link>
                        ) : (
                            formatMessage(messages.errorTooltipBody, {
                                a: chunk => (
                                    <Link
                                        href={ethereumPaymentSectionUrl}
                                        target="_blank"
                                        key="paymentSettingsLink"
                                    >
                                        <span className={styles.noWrap}>{chunk}</span>
                                    </Link>
                                ),
                            })
                        )}
                    </p>
                </div>
            )}
            <ul className={styles.accountMenuItems}>
                <li className={styles.accountMenuItem}>
                    <Link href={ethereumPaymentSectionUrl} buyerLinkType="linkNoDecoration">
                        <FormattedMessage defaultMessage="Wallet" id="nft.connectWallet.wallet" />
                    </Link>
                </li>
                <li className={styles.accountMenuItem}>
                    <Link href="/profile-edit" buyerLinkType="linkNoDecoration">
                        {publicProfile ? (
                            <FormattedMessage
                                defaultMessage="Edit Profile"
                                id="nft.connectWallet.editProfile"
                            />
                        ) : (
                            <FormattedMessage
                                defaultMessage="Create Profile"
                                id="nft.connectWallet.createProfile"
                            />
                        )}
                    </Link>
                </li>
                {publicProfile && (
                    <li className={styles.accountMenuItem}>
                        <Link href="/nft/profile/" buyerLinkType="linkNoDecoration">
                            <FormattedMessage
                                defaultMessage="View Profile"
                                id="nft.connectWallet.viewProfile"
                            />
                        </Link>
                    </li>
                )}
                <li className={styles.accountMenuItem}>
                    <Link
                        onClick={() => logoutUser('/nft-collections/')}
                        buyerLinkType="linkNoDecoration"
                    >
                        <FormattedMessage defaultMessage="Log Out" id="nft.connectWallet.logout" />
                    </Link>
                </li>
                {showCreateButton && (
                    <CreateNftButton
                        fullWidth
                        persistedAddress={persistedAddress || ''}
                        buttonSize="medium"
                        hasProfile={!!publicProfile}
                    />
                )}
            </ul>
            <div className={styles.bottomRow}>
                {showEditorial && (
                    <Link
                        className={styles.accountMenuItem}
                        buyerLinkType="linkNoDecoration"
                        href="/the-mint"
                    >
                        <FormattedMessage
                            defaultMessage="Editorial"
                            id="nft.nav.editorialMenuLink"
                        />
                    </Link>
                )}
                <div className={styles.socialLinks}>
                    <Link href="https://twitter.com/1stDibsNFT" className={styles.socialLink}>
                        <Twitter className={styles.socialIcon} />
                    </Link>
                    <Link href="https://discord.gg/4XhNgZwuBj" className={styles.socialLink}>
                        <DiscordLogo className={styles.socialIcon} />
                    </Link>
                </div>
            </div>
        </>
    );
};
